import React from 'react';
import {Row, Col, DatePicker} from 'antd';
import {Form} from '@ant-design/compatible';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);
import {GetFieldDecoratorOptions} from '@ant-design/compatible/lib/form/Form';
import {Calendar} from '@carbon/icons-react';

interface DateRangePickerProps {
    getFieldDecorator: (field: string, opts: GetFieldDecoratorOptions) => any;
    getFieldValue: (field: string) => any;
    minDate?: dayjs.Dayjs | null;
    dateFromInitialValue?: dayjs.Dayjs | string | null;
    dateFromOnChange?: (value: dayjs.Dayjs) => void | null;
    dateToInitialValue?: dayjs.Dayjs | string | null;
    dateToOnChange?: (value: dayjs.Dayjs) => void | null;
    dateRangeOverrideDays?: number;
    dataTestId?: string;
}

/**
 * Wraps two date pickers in a single component that enforces certain standards for date range selection.
 */
const DateRangePicker: React.FC<DateRangePickerProps> = ({
    getFieldDecorator = () => {},
    getFieldValue = () => {},
    minDate = null,
    dateFromInitialValue = null,
    dateFromOnChange = null,
    dateToInitialValue = null,
    dateToOnChange = null,
    dateRangeOverrideDays,
    dataTestId = '',
}) => {
    const dateFrom: dayjs.Dayjs | undefined = 
        getFieldValue('date_from') || 
        (typeof dateFromInitialValue === 'string' ? dayjs(dateFromInitialValue) : dateFromInitialValue) || 
        dayjs();
    const dateTo: dayjs.Dayjs | undefined = getFieldValue('date_to') || 
        (typeof dateToInitialValue === 'string' ? dayjs(dateToInitialValue) : dateToInitialValue) || 
        dayjs();
    // Disabled logic for date_from picker
    const disabledDateFrom = (current: dayjs.Dayjs) => {
        if (!current) return false;
        // If minDate is set, disallow selecting dates before minDate
        if (minDate && current.isBefore(minDate.startOf('day'))) {
            return true;
        }
        // If a date_to is chosen and dateRangeOverride is specified,
        // limit date_from so it cannot be more than dateRangeOverride days before date_to.
        if (dateTo && dateRangeOverrideDays) {
            const earliestAllowed = dateTo.subtract(dateRangeOverrideDays, 'day');
            if (current.isBefore(earliestAllowed.startOf('day'))) {
                return true;
            }
        }
        // Also ensure date_from cannot be after date_to if date_to is selected
        return dateTo && current.isAfter(dateTo);
    };
    // Disabled logic for date_to picker
    const disabledDateTo = (current: dayjs.Dayjs) => {
        if (!current) return false;
        // date_to should not be before date_from
        if (dateFrom && current.isBefore(dateFrom.startOf('day'))) {
            return true;
        }
        // If date_from and dateRangeOverride exist,
        // ensure date_to does not exceed date_from + dateRangeOverride days
        if (dateFrom && dateRangeOverrideDays) {
            const latestAllowed = dateFrom.add(dateRangeOverrideDays, 'day');
            if (current.isAfter(latestAllowed.endOf('day'))) {
                return true;
            }
        }
        // If there's a minDate, date_to should not be before minDate either.
        return minDate && current.isBefore(minDate.startOf('day'));
    };

    return (
        <Row>
            <Col span={12}>
                <Form.Item label="Date from">
                    {getFieldDecorator('date_from', {initialValue: dateFrom})(
                        <DatePicker
                            format="DD/MM/YYYY"
                            disabledDate={disabledDateFrom}
                            onChange={dateFromOnChange || undefined}
                            allowClear={false}
                            suffixIcon={<Calendar/>}
                            data-testid={dataTestId ? `${dataTestId}_from` : null}
                        />
                    )}
                </Form.Item>
            </Col>
            <Col span={12}>
                <Form.Item label="Date to">
                    {getFieldDecorator('date_to', {initialValue: dateTo})(
                        <DatePicker
                            format="DD/MM/YYYY"
                            disabledDate={disabledDateTo}
                            onChange={dateToOnChange || undefined}
                            allowClear={false}
                            suffixIcon={<Calendar/>}
                            data-testid={dataTestId ? `${dataTestId}_to` : null}
                        />
                    )}
                </Form.Item>
            </Col>
        </Row>
    );
}

export default DateRangePicker;
